import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, type Resolver } from 'react-hook-form'
import { useNavigate } from '@tanstack/react-router'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Form, FormDialog,
} from '@rentflowdev/ui'
import { useCreateBuildProfileMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import { BuilderProfileFormFields } from './fields'
import {
	builderProfileSchema,
	DEFAULT_PROFILE_VALUES,
	formValuesToCreateBody,
	type BuilderProfileFormValues,
} from './schema'

interface BuilderProfileDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

/** Create-profile dialog. Editing lives on the profile details page. */
export function BuilderProfileDialog({ open, onOpenChange }: BuilderProfileDialogProps) {
	const navigate = useNavigate()
	const [createProfile, { isLoading }] = useCreateBuildProfileMutation()
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

	const form = useForm<BuilderProfileFormValues>({
		defaultValues: DEFAULT_PROFILE_VALUES,
		resolver: zodResolver(builderProfileSchema) as Resolver<BuilderProfileFormValues>,
		mode: 'onBlur',
	})

	async function onSubmit(values: BuilderProfileFormValues) {
		setErrorMsg(undefined)
		try {
			const created = await createProfile(formValuesToCreateBody(values)).unwrap()
			form.reset(DEFAULT_PROFILE_VALUES)
			onOpenChange(false)
			if (created?.id) {
				void navigate({ to: '/builder/$profileId', params: { profileId: created.id } })
			}
		} catch (err) {
			console.error('createBuildProfile failed:', err)
			setErrorMsg(extractErrorMessage(err) ?? 'Не удалось создать приложение')
		}
	}

	function handleOpenChange(nextOpen: boolean) {
		if (!nextOpen) {
			setErrorMsg(undefined)
			form.reset(DEFAULT_PROFILE_VALUES)
		}
		onOpenChange(nextOpen)
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={handleOpenChange}
				title="Новое приложение"
				onSubmit={form.handleSubmit(onSubmit)}
				submitLabel="Создать"
				cancelLabel="Отмена"
				closeAriaLabel="Закрыть"
				isSubmitting={isLoading}
				contentClassName="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
			>
				{errorMsg && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertTitle>Ошибка</AlertTitle>
						<AlertDescription>{errorMsg}</AlertDescription>
					</Alert>
				)}
				<BuilderProfileFormFields mode="create" />
			</FormDialog>
		</Form>
	)
}
